import React from "react";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  OutlinedInput,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import { FILTER_DRAWER_WIDTH, PRIMARY_ORANGE } from "./constants";

const FilterDrawer = ({
  open,
  onClose,
  title = "Filters",
  subtitle = "Refine results to find what you need",
  children,
  onApply,
  onReset,
  applyLabel = "Apply",
  resetLabel = "Reset",
}) => (
  <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
    PaperProps={{
      sx: {
        width: { xs: "100%", sm: FILTER_DRAWER_WIDTH },
        bgcolor: "#fff",
      },
    }}
  >
    <Box className="filter-drawer" sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: "rgba(255, 87, 34, 0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <FilterListIcon sx={{ color: PRIMARY_ORANGE, fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1.125rem", color: "#1a1a2e", lineHeight: 1.3 }}>
                {title}
              </Typography>
              {subtitle && (
                <Typography sx={{ fontSize: "0.8rem", color: "#8a8a8a", mt: 0.35, lineHeight: 1.4 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="Close filters"
            size="small"
            sx={{ color: "#666", mt: -0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ borderBottom: "1px solid #eee" }} />

      <Box
        className="filter-drawer-content"
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 2.5,
          py: 2.5,
        }}
      >
        {children}
      </Box>

      <Box sx={{ borderTop: "1px solid #eee" }} />

      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          px: 2.5,
          py: 2,
        }}
      >
        <Button
          variant="outlined"
          fullWidth
          startIcon={<RestartAltIcon sx={{ fontSize: 18 }} />}
          onClick={onReset}
          sx={{
            textTransform: "none",
            borderColor: PRIMARY_ORANGE,
            color: PRIMARY_ORANGE,
            borderRadius: "8px",
            py: 1,
            fontWeight: 500,
            "&:hover": {
              borderColor: "#f4511e",
              bgcolor: "rgba(255, 87, 34, 0.04)",
            },
          }}
        >
          {resetLabel}
        </Button>
        <Button
          variant="contained"
          fullWidth
          startIcon={<FilterListIcon sx={{ fontSize: 18 }} />}
          onClick={onApply}
          sx={{
            textTransform: "none",
            bgcolor: PRIMARY_ORANGE,
            borderRadius: "8px",
            py: 1,
            fontWeight: 500,
            boxShadow: "none",
            "&:hover": { bgcolor: "#f4511e", boxShadow: "none" },
          }}
        >
          {applyLabel}
        </Button>
      </Box>
    </Box>
  </Drawer>
);

const fieldInputSx = {
  borderRadius: "8px",
  bgcolor: "#fff",
  fontSize: "0.875rem",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e5e5" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#d5d5d5" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: PRIMARY_ORANGE },
};

const FilterFieldLabel = ({ children }) => (
  <Typography
    sx={{
      mb: 0.75,
      fontSize: "0.875rem",
      fontWeight: 500,
      color: "#3d3d3d",
    }}
  >
    {children}
  </Typography>
);

const getOptionLabel = (children, selected) => {
  let label = selected;
  React.Children.forEach(children, (child) => {
    if (child?.props?.value === selected) {
      label = child.props.children;
    }
  });
  return label;
};

export const FilterSelectField = ({
  label,
  value,
  onChange,
  children,
  placeholder,
}) => {
  const emptyPlaceholder =
    placeholder || `Select ${label ? label.toLowerCase() : "option"}`;

  return (
    <Box className="filter-field" sx={{ mb: 2.5 }}>
      <FilterFieldLabel>{label}</FilterFieldLabel>
      <TextField
        select
        fullWidth
        size="small"
        value={value}
        onChange={onChange}
        SelectProps={{
          displayEmpty: true,
          renderValue: (selected) => {
            if (selected === "" || selected === null || selected === undefined) {
              return (
                <Box component="span" sx={{ color: "#9e9e9e" }}>
                  {emptyPlaceholder}
                </Box>
              );
            }
            return getOptionLabel(children, selected);
          },
        }}
        sx={fieldInputSx}
      >
        {children}
      </TextField>
    </Box>
  );
};

export const FilterDateField = ({ label, value, onChange }) => (
  <Box className="filter-field" sx={{ mb: 2.5 }}>
    <FilterFieldLabel>{label}</FilterFieldLabel>
    <TextField
      type="date"
      fullWidth
      size="small"
      value={value || ""}
      onChange={onChange}
      sx={fieldInputSx}
    />
  </Box>
);

export const FilterTextField = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  withSearchIcon = false,
}) => (
  <Box className="filter-field" sx={{ mb: 2.5 }}>
    <FilterFieldLabel>{label}</FilterFieldLabel>
    <OutlinedInput
      fullWidth
      size="small"
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      startAdornment={
        withSearchIcon ? (
          <SearchIcon sx={{ fontSize: 18, color: "#9e9e9e", mr: 1 }} />
        ) : undefined
      }
      sx={{
        ...fieldInputSx,
        px: 1.25,
        py: 0.85,
      }}
    />
  </Box>
);

export default FilterDrawer;
